import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { ArrowRight } from 'lucide-react';

const WowSection = () => {
  const { language } = useLanguage();
  const fr = language === 'fr';
  const sectionRef = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); observer.disconnect(); } },
      { rootMargin: '-80px', threshold: 0.05 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const cls = (delay) =>
    `wow-fade-up${visible ? ' wow-visible' : ''}`;
  const style = (delay) => ({ animationDelay: `${delay}s` });

  return (
    <section
      ref={sectionRef}
      className="wow-section text-center"
      style={{ backgroundColor: '#050505', padding: '120px 8% 80px' }}
      data-testid="wow-section"
    >
      <div style={{ maxWidth: 860, margin: '0 auto' }}>

        {/* BLOC 1 — Réalité */}
        <p
          className={cls()}
          style={{
            ...style(0.1),
            fontSize: 'clamp(1.6rem, 4vw, 2.8rem)',
            lineHeight: 1.2,
            fontWeight: 500,
            color: 'rgba(255,255,255,0.9)',
            letterSpacing: '-0.02em',
          }}
          data-testid="wow-reality-1"
        >
          {fr
            ? 'Un client achète un véhicule tous les 4 à 8 ans.'
            : 'A client buys a vehicle every 4 to 8 years.'}
        </p>

        <p
          className={cls()}
          style={{
            ...style(0.3),
            fontSize: 'clamp(1.6rem, 4vw, 2.8rem)',
            lineHeight: 1.2,
            fontWeight: 600,
            color: '#ffffff',
            letterSpacing: '-0.02em',
            marginTop: 12,
          }}
          data-testid="wow-reality-2"
        >
          {fr
            ? 'Nous opérons sur le marché automobile, tous les jours.'
            : 'We operate in the automotive market, every day.'}
        </p>

        <div style={{ height: 64 }} />

        {/* BLOC 2 — Importance */}
        <p
          className={cls()}
          style={{
            ...style(0.15),
            fontSize: 'clamp(1.1rem, 2.5vw, 1.5rem)',
            lineHeight: 1.6,
            fontWeight: 400,
            color: 'rgba(255,255,255,0.9)',
            letterSpacing: '-0.01em',
          }}
          data-testid="wow-importance-1"
        >
          {fr
            ? "C'est le deuxième achat le plus important de votre vie."
            : "It's the second most important purchase of your life."}
        </p>

        <div
          className={cls()}
          style={{ ...style(0.3), marginTop: 20 }}
        >
          <p
            style={{
              fontSize: 'clamp(1.1rem, 2.5vw, 1.5rem)',
              lineHeight: 1.6,
              fontWeight: 400,
              color: 'rgba(255,255,255,0.9)',
              letterSpacing: '-0.01em',
            }}
            data-testid="wow-importance-2"
          >
            {fr
              ? 'Vous vous entourez pour votre immobilier.'
              : 'You surround yourself with experts for real estate.'}
          </p>
          <p
            style={{
              fontSize: 'clamp(1.1rem, 2.5vw, 1.5rem)',
              lineHeight: 1.6,
              fontWeight: 400,
              color: 'rgba(255,255,255,0.9)',
              letterSpacing: '-0.01em',
            }}
            data-testid="wow-importance-3"
          >
            {fr
              ? 'Vous vous entourez pour vos finances.'
              : 'You surround yourself with experts for your finances.'}
          </p>
        </div>

        <p
          className={cls()}
          style={{
            ...style(0.45),
            fontSize: 'clamp(1.2rem, 2.8vw, 1.65rem)',
            lineHeight: 1.5,
            fontWeight: 600,
            color: '#ffffff',
            letterSpacing: '-0.01em',
            marginTop: 24,
          }}
          data-testid="wow-importance-highlight"
        >
          {fr
            ? 'Vous méritez le même niveau de clarté ici.'
            : 'You deserve the same level of clarity here.'}
        </p>

        <div style={{ height: 56 }} />

        {/* BLOC 3 — Réassurance */}
        <div
          className={cls()}
          style={{
            ...style(0.15),
            fontSize: 'clamp(1.1rem, 2.5vw, 1.5rem)',
            lineHeight: 1.7,
            fontWeight: 400,
            color: 'rgba(255,255,255,0.92)',
            letterSpacing: '-0.01em',
          }}
          data-testid="wow-reassurance"
        >
          <p>{fr ? 'Nous simplifions.' : 'We simplify.'}</p>
          <p>{fr ? 'Nous structurons.' : 'We structure.'}</p>
          <p>
            {fr
              ? 'Nous vous accompagnons, du début à la fin.'
              : 'We guide you, from start to finish.'}
          </p>
        </div>

        <p
          className={cls()}
          style={{
            ...style(0.35),
            fontSize: 'clamp(1.2rem, 2.8vw, 1.65rem)',
            lineHeight: 1.5,
            fontWeight: 600,
            color: '#ffffff',
            letterSpacing: '-0.01em',
            marginTop: 28,
          }}
          data-testid="wow-sans-pression"
        >
          {fr ? 'Sans pression.' : 'No pressure.'}
        </p>

        <div style={{ height: 56 }} />

        {/* BLOC 4 — Autorité */}
        <p
          className={cls()}
          style={{
            ...style(0.15),
            fontSize: 'clamp(1.3rem, 3vw, 2rem)',
            lineHeight: 1.3,
            fontWeight: 600,
            color: 'rgba(255,255,255,0.95)',
            letterSpacing: '-0.02em',
          }}
          data-testid="wow-authority"
        >
          {fr
            ? 'Un bon service se mesure en erreurs évitées.'
            : 'A good service is measured by mistakes avoided.'}
        </p>

        {/* CTA */}
        <div
          className={cls()}
          style={{ ...style(0.3), marginTop: 48 }}
          data-testid="wow-cta-container"
        >
          <Link
            to="/rendez-vous"
            className="inline-flex items-center gap-3"
            style={{
              backgroundColor: '#ffffff',
              color: '#000000',
              padding: '15px 30px',
              borderRadius: 50,
              fontWeight: 600,
              fontSize: '0.95rem',
              textDecoration: 'none',
              boxShadow: '0 2px 12px rgba(0,0,0,0.25)',
              transition: 'opacity 0.3s ease, transform 0.3s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.opacity = '0.9';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.opacity = '1';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
            data-testid="wow-cta"
          >
            {fr ? 'Planifier une consultation stratégique' : 'Schedule a strategic consultation'}
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

      </div>
    </section>
  );
};

export default WowSection;
